import { useCallback, useEffect, useState } from "react";
import API from "../config/api";

// ─────────────────────────────────────────────────────────────────────────────
// Cookie helpers
// ─────────────────────────────────────────────────────────────────────────────
function getCookie(name) {
  return decodeURIComponent(
    document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))?.[1] ?? ""
  );
}
function setCookie(name, value, days = 30) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/`;
}

const COOKIE_KEY = "_draft_image_carousel_pre_selected_images";

function readIds() {
  const raw = getCookie(COOKIE_KEY);
  if (!raw) return [];
  try { return JSON.parse(raw) || []; } catch { return []; }
}
function writeIds(ids) {
  setCookie(COOKIE_KEY, JSON.stringify(ids));
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function DraftPromptImagePreSelected() {
  const [ids, setIds] = useState(() => readIds());

  // Sync checkboxes and cookie, expose on window
  const refresh = useCallback(() => {
    const current = readIds();

    // Check any newly checked checkboxes on the page
    document.querySelectorAll(".image_carousel_checkbox:checked").forEach((el) => {
      const val = el.value;
      if (!current.includes(val)) current.push(val);
    });

    writeIds(current);
    setIds([...current]);

    // Sync checkbox states
    document.querySelectorAll(".image_carousel_checkbox").forEach((el) => {
      el.checked = current.includes(el.value);
    });
  }, []);

  const remove = useCallback((idimage) => {
    const next = readIds().filter((id) => id !== idimage);
    writeIds(next);
    setIds(next);
    const cb = document.querySelector(`.image_carousel_checkbox[value="${idimage}"]`);
    if (cb) cb.checked = false;
  }, []);

  const clear = useCallback(() => {
    writeIds([]);
    setIds([]);
    document.querySelectorAll(".image_carousel_checkbox").forEach((el) => {
      el.checked = false;
    });
  }, []);

  const use = useCallback(() => {
    const current = readIds();
    current.forEach((idimage) => {
      window._draft_prompt_item_add?.(
        "image",
        idimage,
        `upload/image/${idimage}/image/original.jpg`
      );
    });
    clear();
  }, [clear]);

  // Expose on window for legacy onclick handlers
  useEffect(() => {
    window._draft_prompt_image_carousel_pre_selected_check  = refresh;
    window._draft_prompt_image_carousel_pre_selected_remove = remove;
    window._draft_prompt_image_carousel_pre_selected_clear  = clear;
    window._draft_prompt_image_carousel_pre_selected_use    = use;
    return () => {
      delete window._draft_prompt_image_carousel_pre_selected_check;
      delete window._draft_prompt_image_carousel_pre_selected_remove;
      delete window._draft_prompt_image_carousel_pre_selected_clear;
      delete window._draft_prompt_image_carousel_pre_selected_use;
    };
  }, [refresh, remove, clear, use]);

  // Reload from cookie on window focus (multi-tab sync)
  useEffect(() => {
    const onFocus = () => setIds(readIds());
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  if (!ids.length) return null;

  return (
    <div
      className="position-fixed bottom-0 start-50 translate-middle-x mb-5"
      style={{ zIndex: 4 }}
    >
      <div className="btn-group shadow" role="group">
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={use}
        >
          + Use
        </button>

        <div className="border-top border-bottom list bg-white d-flex align-items-center px-0">
          {ids.map((idimage) => (<div key={idimage} className="position-relative d-inline-block"><a
                href="#remove"
                title="Remove"
                onClick={(e) => { e.preventDefault(); remove(idimage); }}
              ><img
                  src={`${API.baseUrl}/upload/image/${idimage}/image/50x50.jpg`}
                  className="img-fluid"
                  width="50"
                  height="50"
                  onError={(e) => { e.target.src = "/assets/img/50x50.png"; }}
                  alt=""
                /></a></div>))}</div>

        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={clear}
        >
          Clear
        </button>
      </div>
    </div>
  );
}